import React from 'react';

import {
  CmBaseTable,
  CmBox,
  CmButton,
  CmHeading,
  CmList,
  CmListItem,
  CmModalBody,
  CmModalFooter,
  CmTableBodyRow,
  CmTd,
  CmText,
  CmTooltip,
} from '@/components/uiParts';
import { CurrencyDomainService, DateDomainService } from '@/domain/services';
import { nonNullableStr, ommisionText } from '@/lib/util';
import { PreviewBulkUpsertBrandItemsResponse } from '@/services/BrandItem/hooks/upload/useMenuManagementPreviewBulkUpsertBrandItems';

import { itemTableThs } from '../../items/service';

type Props = {
  insertCount: PreviewBulkUpsertBrandItemsResponse['insert_count'];
  updateCount: PreviewBulkUpsertBrandItemsResponse['update_count'];
  errorsByLine: PreviewBulkUpsertBrandItemsResponse['errors_by_line'];
  brandItems: PreviewBulkUpsertBrandItemsResponse['brand_items'];
  handleClickImport: () => void;
  isDisableImport: boolean;
  isLoadingImport: boolean;
  handleClickCancel: () => void;
};

const TABLE_THS = itemTableThs.filter((e) => e.label !== 'アクション');

const PreviewCsv: React.FC<Props> = (props: Props) => {
  const errorLines = Object.keys(props.errorsByLine);
  const hasError = errorLines.length > 0;

  return (
    <>
      <CmModalBody>
        <CmBox>
          <CmHeading>インポート対象</CmHeading>
          <CmList>
            <CmListItem>
              <CmText>追加：{props.insertCount}件</CmText>
            </CmListItem>
            <CmListItem>
              <CmText>更新：{props.updateCount}件</CmText>
            </CmListItem>
          </CmList>
        </CmBox>
        {hasError && (
          <CmBox marginTop={5}>
            <CmText fontWeight="bold">
              エラー行：
              {errorLines
                .map(
                  (line) => `${line}: (${props.errorsByLine[line].join(', ')})`
                )
                .join(', ')}
            </CmText>
            <CmText fontSize="small">※エラー行はインポートされません</CmText>
          </CmBox>
        )}
        <CmBox marginTop={5}>
          <CmBaseTable ths={TABLE_THS}>
            {props.brandItems.map((item, i) => {
              return (
                <CmTableBodyRow key={i + 1}>
                  <CmTd>{item.name}</CmTd>
                  <CmTd>{nonNullableStr(item.order_methods)}</CmTd>
                  <CmTd>
                    {item.image_url !== null ? '設定あり' : '設定なし'}
                  </CmTd>
                  <CmTd>
                    {item.takeout_price !== null &&
                      CurrencyDomainService.priceFormat(item.takeout_price)}
                  </CmTd>
                  <CmTd>
                    {item.eat_in_price !== null &&
                      CurrencyDomainService.priceFormat(item.eat_in_price)}
                  </CmTd>
                  <CmTd>
                    {item.delivery_price !== null &&
                      CurrencyDomainService.priceFormat(item.delivery_price)}
                  </CmTd>
                  <CmTd>
                    <CmTooltip label={nonNullableStr(item.category_names)}>
                      {ommisionText(
                        nonNullableStr(item.category_names ?? ''),
                        20
                      )}
                    </CmTooltip>
                  </CmTd>
                  <CmTd>
                    <CmTooltip label={nonNullableStr(item.option_group_names)}>
                      {ommisionText(
                        nonNullableStr(item.option_group_names ?? ''),
                        20
                      )}
                    </CmTooltip>
                  </CmTd>
                  <CmTd>
                    <CmTooltip label={item.memo ?? ''}>
                      {ommisionText(item.memo ?? '', 20)}
                    </CmTooltip>
                  </CmTd>
                  <CmTd textAlign="center">
                    <CmTooltip label={item.updated_at}>
                      {DateDomainService.datetimeFormat(
                        new Date(item.updated_at)
                      )}
                    </CmTooltip>
                  </CmTd>
                </CmTableBodyRow>
              );
            })}
          </CmBaseTable>
        </CmBox>
      </CmModalBody>
      <CmModalFooter display="flex" alignItems="center">
        <CmButton
          variant="outline"
          mr={3}
          disabled={props.isLoadingImport}
          onClick={props.handleClickCancel}
        >
          キャンセル
        </CmButton>
        <CmButton
          disabled={props.isDisableImport}
          isLoading={props.isLoadingImport}
          onClick={props.handleClickImport}
        >
          商品をインポートする
        </CmButton>
      </CmModalFooter>
    </>
  );
};

export default PreviewCsv;
